import themes from 'devextreme/ui/themes';
import { refreshTheme } from 'devextreme/viz/themes';
import type { ThemeMode, ThemePreferences } from './themeCatalog';
import { resolveHtmlDataTheme, resolveThemeId } from './themeResolve';
import { bootstrapThemeRegistry } from './themeRegistry';
import { broadcastThemeToChildFrames, postThemeToWindow } from './themeSync';

export type {
  ThemeMode,
  ThemePreferences,
  ThemeFamily,
  MaterialAccent,
  FluentVariant,
  GenericPalette
} from './themeCatalog';

let lastAppliedThemeId: string | null = null;
let lastAppliedPreferences: ThemePreferences | null = null;

export function getLastAppliedThemePreferences(): ThemePreferences | null {
  return lastAppliedPreferences;
}

export function syncThemeToFrame(frame: Window | null | undefined): void {
  if (lastAppliedPreferences) postThemeToWindow(frame, lastAppliedPreferences);
}

export function ensureThemeRegistry(): void {
  bootstrapThemeRegistry();
}

export function applyThemePreferences(prefs: ThemePreferences): void {
  ensureThemeRegistry();

  const themeId = resolveThemeId(prefs);
  const htmlMode = resolveHtmlDataTheme(prefs, themeId);
  lastAppliedPreferences = prefs;

  document.documentElement.setAttribute('data-theme', htmlMode);
  document.documentElement.setAttribute('data-theme-family', prefs.family);
  document.dispatchEvent(new Event('jarvis:theme-change'));
  if (prefs.family === 'material') {
    document.documentElement.setAttribute('data-accent', prefs.materialAccent);
  } else {
    document.documentElement.removeAttribute('data-accent');
  }

  // Skip re-entrant DevExtreme theme swaps when nothing changed (avoids React 19 #185 loops).
  const themeChanged = lastAppliedThemeId !== themeId;
  if (themeChanged) {
    try {
      themes.current(themeId);
      lastAppliedThemeId = themeId;
    } catch (err) {
      console.warn(
        `[theme] Failed to apply DevExtreme theme "${themeId}". Check /devextreme/css assets and CSP.`,
        err
      );
    }

    themes.ready(() => {
      try {
        refreshTheme();
      } catch (err) {
        console.warn('[theme] refreshTheme failed', err);
      }
    });

    broadcastThemeToChildFrames(prefs);
  }
}

/** @deprecated Use applyThemePreferences — kept for thin app wrappers during transition */
export function applyTheme(mode: ThemeMode): void {
  applyThemePreferences({
    family: 'material',
    materialAccent: 'blue',
    genericPalette: 'light',
    genericCompact: false,
    fluentVariant: 'blue',
    fluentCompact: false,
    mode
  });
}
